import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { assignSponsorLocation, createSponsor, getSponsorDetails, getSponsorQuota, getSponsors, removeSponsorLocation } from '../../redux/sponsors/sponsorSlice';
import { getRoles } from '../../redux/role/roleSlice';
import { getEventLocations } from '../../redux/eventLocation/eventLocationSlice';
import { getLoggedInUserInfo } from '../../redux/auth/authApi';
import { truncateText } from "../../helper/helper";
import Select from "react-select";
import { countryCodes } from "../../helper/countryCodes";
import { getUserCountry } from "../../helper/getUserCountry";
import { countryOptions, customStyles, formatOptionLabel } from '../../components/CountryCode/helper';
import PaginationHelper from '../../helper/PaginationHelper';

const SponsorLayer = () => {

    const dispatch = useDispatch();

    const { sponsors, sponsorDetails, loggedInSponsorQuota, total } = useSelector((state) => state.sponsor);
    const { locations = [] } = useSelector((state) => state.eventLocation);

    const [form, setForm] = useState({
        fullName: "",
        email: "",
        phone: "",
        countryCode: "+91",
        designation: "",
        country: "",
        city: ""
    });

    const [search, setSearch] = useState("");
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [assignForm, setAssignForm] = useState({});
    const [confirmAssign, setConfirmAssign] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [selectedSponsor, setSelectedSponsor] = useState(null);
    const [localAssignedLocations, setLocalAssignedLocations] = useState({});

    const totalEntries = total;
    const totalPages = Math.ceil(totalEntries / entries);

    const startIndex = (currentPage - 1) * entries;
    const endIndex = startIndex + entries;
    const loggedInUserInfo = getLoggedInUserInfo();
    const loggedInSponsorId = loggedInUserInfo?.id;

    useEffect(() => {
        const detectCountry = async () => {
            const data = await getUserCountry();

            if (data) {
                const matched = countryCodes.find(
                    (c) => c.iso2 === data.iso2
                );

                if (matched) {
                    setForm((prev) => ({
                        ...prev,
                        countryCode: matched.dialCode,
                        country: matched.name
                    }));
                }
            }
        };

        detectCountry();
    }, []);

    useEffect(() => {
        dispatch(getSponsors({ page: currentPage, limit: entries, search }));
        dispatch(getEventLocations());
    }, [dispatch, currentPage, entries, search]);

    useEffect(() => {
        if (loggedInSponsorId) {
            dispatch(getSponsorDetails(loggedInSponsorId));
            dispatch(getSponsorQuota(loggedInSponsorId));
        }
    }, [dispatch, loggedInSponsorId]);

    useEffect(() => {
        sponsors.forEach((item) => {
            if (!getSponsorLocations(item).length && !sponsorDetails[item.id]) {
                dispatch(getSponsorDetails(item.id));
            }
        });
    }, [dispatch, sponsors, sponsorDetails]);

    function getSponsorLocations(item) {
        const details = sponsorDetails[item.id] || {};
        return (
            localAssignedLocations[item.id] ||
            item.eventLocations ||
            item.EventLocations ||
            item.locations ||
            details.eventLocations ||
            details.EventLocations ||
            details.locations ||
            details.user?.eventLocations ||
            details.user?.EventLocations ||
            details.user?.locations ||
            details.User?.eventLocations ||
            details.User?.EventLocations ||
            details.User?.locations ||
            []
        );
    }

    const refreshSponsors = () => {
        dispatch(getSponsors({ page: currentPage, limit: entries, search }));
    };

    const quotaItems = Array.isArray(loggedInSponsorQuota)
        ? loggedInSponsorQuota
        : loggedInSponsorQuota?.items || loggedInSponsorQuota?.quotas || [];
    const assignedQuota = quotaItems.reduce((sum, item) => sum + Number(item?.quota || item?.assignedQuota || item?.totalQuota || 0), 0);
    const loggedInSponsorDetails = sponsorDetails[loggedInSponsorId] || loggedInUserInfo || {};
    const loggedInLocations = getSponsorLocations(loggedInSponsorDetails);
    const quotaList = quotaItems.map((item) => {
        const locationId = item.locationId || item.eventLocationId;
        const locationName =
            item.location?.name ||
            item.EventLocation?.name ||
            item.eventLocation?.name ||
            locations.find((loc) => loc.id === locationId)?.name ||
            "Location";

        return {
            name: locationName,
            quota: item?.quota ?? item?.assignedQuota ?? item?.totalQuota ?? 0
        };
    });
    const locationList = loggedInLocations.map((loc) =>
        loc.name || locations.find((item) => item.id === loc.id)?.name || "Location"
    );

    const handleLocationSelect = (item, locationId) => {
        const location = locations.find((loc) => loc.id === locationId);
        setAssignForm((prev) => ({
            ...prev,
            [item.id]: { locationId }
        }));
        if (location) setConfirmAssign({ item, location });
    };

    const handleAssignLocation = async () => {
        try {
            await dispatch(assignSponsorLocation({
                userId: confirmAssign.item.id,
                locationId: confirmAssign.location.id
            })).unwrap();
            toast.success("Location assigned");
            setAssignForm((prev) => ({ ...prev, [confirmAssign.item.id]: { locationId: "" } }));
            setLocalAssignedLocations((prev) => ({
                ...prev,
                [confirmAssign.item.id]: [
                    ...getSponsorLocations(confirmAssign.item).filter((loc) => loc.id !== confirmAssign.location.id),
                    confirmAssign.location
                ]
            }));
            setConfirmAssign(null);
            dispatch(getSponsorDetails(confirmAssign.item.id));
            refreshSponsors();
        } catch (err) {
            toast.error(err?.message || "Failed");
        }
    };

    const handleRemoveLocation = async () => {
        try {
            await dispatch(removeSponsorLocation({
                userId: confirmRemove.item.id,
                locationId: confirmRemove.location.id
            })).unwrap();
            toast.success("Location removed");
            setLocalAssignedLocations((prev) => ({
                ...prev,
                [confirmRemove.item.id]: getSponsorLocations(confirmRemove.item).filter(
                    (loc) => loc.id !== confirmRemove.location.id
                )
            }));
            setConfirmRemove(null);
            dispatch(getSponsorDetails(confirmRemove.item.id));
            refreshSponsors();
        } catch (err) {
            toast.error(err?.message || "Failed");
        }
    };

    const handleSubmit = () => {

        if (!form.fullName || !form.fullName.trim()) {
            toast.error("Full Name is required");
            return;
        }

        if (form.fullName.length > 100) {
            toast.error("Full Name max 100 characters");
            return;
        }

        if (!form.email || !form.email.trim()) {
            toast.error("Email is required");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(form.email)) {
            toast.error("Enter valid email");
            return;
        }

        if (!form.phone) {
            toast.error("Phone is required");
            return;
        }

        if (!/^\d{6,15}$/.test(form.phone)) {
            toast.error("Enter valid phone number");
            return;
        }

        if (!form.designation || !form.designation.trim()) {
            toast.error("Designation is required");
            return;
        }

        if (form.designation.length > 100) {
            toast.error("Designation max 100 characters");
            return;
        }

        const payload = {
            ...form,
            fullName: form.fullName.trim(),
            email: form.email.trim(),
            designation: form.designation.trim(),
            country: form.country?.trim(),
            city: form.city?.trim(),
            countryCode: form.countryCode
        };

        dispatch(createSponsor(payload))
            .unwrap()
            .then((res) => {
                toast.success(res?.message || "User created successfully");

                setForm({
                    fullName: "",
                    email: "",
                    phone: "",
                    countryCode: "+91",
                    designation: "",
                    country: "",
                    city: ""
                });

                dispatch(getSponsors({ page: currentPage, limit: entries, search }));

                document.querySelector("#sponsorModal .btn-close")?.click();
            })
            .catch((err) => {
                toast.error(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Something went wrong"
                );
            });
    };

    return (
        <>
            <div className="card bg-base shadow-sm radius-16 p-24 mb-24" style={{ border: "1px solid #cfd4e4" }}>
                <div className="row gy-3">
                    {[
                        { label: "Total Users", value: total || 0, icon: "mdi:account-group-outline", color: "#71cf45", bg: "#ecfae8", list: [] },
                        { label: "Active Users", value: sponsors.filter((item) => item.status).length, icon: "mdi:account-check-outline", color: "#0b3fa5", bg: "#e9eefc", list: [] },
                        { label: "Assigned Quota", value: assignedQuota, icon: "mdi:ticket-confirmation-outline", color: "#0b3fa5", bg: "#eef2ff", list: quotaList.map((item) => `${item.name}|${item.quota}`) },
                        { label: "Assigned Locations", value: loggedInLocations.length, icon: "mdi:map-marker-outline", color: "#f59e0b", bg: "#fff4dc", list: locationList },
                    ].map((card) => (
                        <div className="col-xl-3 col-md-6" key={card.label}>
                            <div
                                className="position-relative h-100 radius-10 p-20 bg-neutral-50 overflow-hidden shadow-sm"
                                style={{ border: "1px solid #dde1eb", transition: "0.2s ease", boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)" }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-3px)";
                                    e.currentTarget.style.boxShadow = "0 12px 26px rgba(15, 23, 42, 0.1)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 8px 20px rgba(15, 23, 42, 0.08)";
                                }}
                            >
                                <div className="position-absolute top-0 start-0 w-100 h-4-px" style={{ backgroundColor: card.color }} />
                                <div className="d-flex align-items-center gap-12 mb-22">
                                    <div className="w-42-px h-42-px rounded-circle d-flex align-items-center justify-content-center" style={{ backgroundColor: card.bg, color: card.color }}>
                                        <Icon icon={card.icon} width="20" />
                                    </div>
                                    <div className="text-uppercase fw-semibold" style={{ color: "#1f2937", fontSize: 13, letterSpacing: 0.5 }}>
                                        {card.label}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="fw-normal mb-10" style={{ color: "#0f172a", fontSize: 30, lineHeight: 1 }}>
                                        {card.value}
                                    </div>
                                    <div className="d-flex flex-column align-items-center gap-6">
                                        {card.list.slice(0, 3).map((item) => {
                                            const [name, count] = item.split("|");
                                            return (
                                                <span key={item} className="bg-base border radius-8 px-10 py-0 d-inline-flex align-items-center gap-10" style={{ fontSize: 10, minWidth: card.label === "Assigned Quota" ? 160 : "auto" }}>
                                                    <span>{truncateText(name, 18)}</span>
                                                    {count && <span className="fw-bold ms-auto">{count}</span>}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="card h-100 p-0 radius-12 shadow-sm border-0">
                {/* HEADER */}
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between">

                    <div className="d-flex gap-3">

                        <select
                            className="form-select form-select-sm w-auto"
                            value={entries}
                            onChange={(e) => {
                                setEntries(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                        </select>

                        <input
                            className="form-control w-auto"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setCurrentPage(1);
                            }}
                        />

                    </div>

                    <button
                        className="btn btn-primary"
                        data-bs-toggle="modal"
                        data-bs-target="#sponsorModal"
                    >
                        + Add Representative
                    </button>

                </div>

                {/* TABLE */}
                <div className="card-body p-12">
                    <table className="table bordered-table">

                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Full Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Designation</th>
                                <th>City</th>
                                <th>Country</th>
                                <th>Status</th>
                                <th>Created At</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {sponsors.map((item, index) => (
                                <tr key={item.id} className="align-middle">
                                    <td>
                                        <span className="fw-semibold text-secondary">
                                            {(currentPage - 1) * entries + index + 1}
                                        </span>
                                    </td>

                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center fw-bold">
                                                {(item.fullName || "U").charAt(0).toUpperCase()}
                                            </div>
                                            <span className="fw-semibold">{item.fullName || "-"}</span>
                                        </div>
                                    </td>
                                    <td className="text-secondary-light">{item.email || "-"}</td>
                                    <td className="text-secondary-light">{item.phone || "-"}</td>
                                    <td>{truncateText(item.designation || "-")}</td>
                                    <td className="text-secondary-light">{item.city || "-"}</td>
                                    <td className="text-secondary-light">{item.country || "-"}</td>

                                    <td>
                                        <span className={`badge ${item.status ? "bg-success" : "bg-danger"}`}>
                                            {item.status ? "Active" : "Inactive"}
                                        </span>
                                    </td>

                                    <td>
                                        {item.createdAt
                                            ? new Date(item.createdAt).toLocaleDateString()
                                            : "-"}
                                    </td>

                                    <td>
                                        <button
                                            type="button"
                                            className="w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                            data-bs-toggle="modal"
                                            data-bs-target="#sponsorViewModal"
                                            onClick={() => {
                                                setSelectedSponsor(item);
                                                dispatch(getSponsorDetails(item.id));
                                            }}
                                        >
                                            <Icon icon="iconamoon:eye-light" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>

                    </table>

                    {/* PAGINATION */}
                    <PaginationHelper
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalItems={total}
                        pageSize={entries}
                    />

                </div>
            </div>

            {confirmAssign && (
                <div className="custom-modal" style={{ zIndex: 2000 }}>
                    <div className="modal-content-box">
                        <div className="modal-header">
                            <h6>Assign Location</h6>
                            <Icon
                                icon="mdi:close"
                                className="cursor-pointer"
                                onClick={() => setConfirmAssign(null)}
                            />
                        </div>
                        <div className="modal-body text-center">
                            <Icon icon="mdi:map-marker-radius" width="50" color="#4CAF50" />
                            <p className="mt-10">Assign this location?</p>
                            <h6 className="fw-bold">{confirmAssign.location.name}</h6>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-light" onClick={() => setConfirmAssign(null)}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleAssignLocation}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmRemove && (
                <div className="custom-modal danger" style={{ zIndex: 2000 }}>
                    <div className="modal-content-box">
                        <div className="modal-header">
                            <h6>Remove Location</h6>
                            <Icon
                                icon="mdi:close"
                                className="cursor-pointer"
                                onClick={() => setConfirmRemove(null)}
                            />
                        </div>
                        <div className="modal-body text-center">
                            <Icon icon="mdi:alert-circle" width="50" color="#f44336" />
                            <p className="mt-10">Remove this location?</p>
                            <h6 className="fw-bold">{confirmRemove.location.name}</h6>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-light" onClick={() => setConfirmRemove(null)}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={handleRemoveLocation}>
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="modal fade" id="sponsorViewModal">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content border-0 radius-16 overflow-hidden">
                        <div className="modal-header border-0 bg-primary-50 px-24 py-20">
                            <h5 className="mb-0 text-primary-600">Representative Details</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        {selectedSponsor && (
                            <div className="modal-body p-24">
                                <div className="bg-base radius-12 border p-20 mb-24 d-flex align-items-center justify-content-between gap-3 flex-wrap">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-64-px h-64-px rounded-circle bg-success-focus text-success-main d-flex justify-content-center align-items-center fw-bold fs-3">
                                            {(selectedSponsor.fullName || "U").charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h5 className="mb-4">{selectedSponsor.fullName || "-"}</h5>
                                            <span className="text-secondary-light">{selectedSponsor.email || "-"}</span>
                                        </div>
                                    </div>
                                    <span className={`badge px-12 py-8 ${selectedSponsor.status ? "bg-success" : "bg-danger"}`}>
                                            {selectedSponsor.status ? "Active" : "Inactive"}
                                    </span>
                                </div>

                                <div className="row gy-3 mb-24">
                                    <div className="col-md-6">
                                        <div className="border radius-8 p-16 h-100">
                                            <span className="text-secondary-light d-block mb-4">Phone</span>
                                            <span className="fw-semibold">{selectedSponsor.countryCode || ""} {selectedSponsor.phone || "-"}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border radius-8 p-16 h-100">
                                            <span className="text-secondary-light d-block mb-4">Designation</span>
                                            <span className="fw-semibold">{selectedSponsor.designation || "-"}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border radius-8 p-16 h-100">
                                            <span className="text-secondary-light d-block mb-4">Badge ID</span>
                                            <span className="fw-semibold">{selectedSponsor.badgeId || "-"}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="border radius-8 p-16 h-100">
                                            <span className="text-secondary-light d-block mb-4">Location</span>
                                            <span className="fw-semibold">{[selectedSponsor.city, selectedSponsor.country].filter(Boolean).join(", ") || "-"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="border radius-12 p-20 bg-neutral-50">
                                    <div className="d-flex justify-content-between align-items-center gap-3 mb-16">
                                        <h6 className="mb-0 text-primary-600">Assigned Locations</h6>
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ maxWidth: 280 }}
                                            value={assignForm[selectedSponsor.id]?.locationId || ""}
                                            onChange={(e) => handleLocationSelect(selectedSponsor, e.target.value)}
                                        >
                                            <option value="">Assign location</option>
                                            {locations.map((loc) => (
                                                <option key={loc.id} value={loc.id}>{loc.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                        {getSponsorLocations(selectedSponsor).length ? (
                                            getSponsorLocations(selectedSponsor).map((loc) => (
                                                <span key={loc.id} className="badge bg-success-focus text-success-main d-inline-flex align-items-center gap-1 px-12 py-8 rounded-pill">
                                                    {loc.name || locations.find((item) => item.id === loc.id)?.name || "Location"}
                                                    <Icon
                                                        icon="mdi:close"
                                                        className="cursor-pointer text-danger-main"
                                                        onClick={() => setConfirmRemove({ item: selectedSponsor, location: loc })}
                                                    />
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-secondary-light">No locations assigned</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL */}
            <div className="modal fade" id="sponsorModal">

                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5>Add Representative</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body">

                            <div className="row">

                                <div className="col-md-6 mb-3">
                                    <label>
                                        Full Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        className="form-control"
                                        maxLength={100}
                                        placeholder="Enter full name"
                                        value={form.fullName}
                                        onChange={(e) =>
                                            setForm({ ...form, fullName: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>
                                        Email <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        className="form-control"
                                        type="email"
                                        placeholder="Enter email"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({ ...form, email: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>Phone <span className="text-danger">*</span></label>
                                    <div style={{ display: "flex", gap: "10px" }}>

                                        <div style={{ width: "200px" }}>
                                            <Select
                                                options={countryOptions}
                                                value={countryOptions.find(c => c.value === form.countryCode)}
                                                onChange={(selected) => {
                                                    const code = selected?.value.replace(/\D/g, "").slice(0, 4);

                                                    setForm((prev) => ({
                                                        ...prev,
                                                        countryCode: "+" + code
                                                    }));
                                                }}
                                                isSearchable
                                                styles={customStyles}
                                                formatOptionLabel={formatOptionLabel}
                                            />
                                        </div>

                                        <input
                                            className="form-control"
                                            type="text"
                                            maxLength={15}
                                            placeholder="Enter phone"
                                            value={form.phone}
                                            onChange={(e) => {
                                                const numericValue = e.target.value.replace(/\D/g, "").slice(0, 15);

                                                setForm({ ...form, phone: numericValue });
                                            }}
                                        />

                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label>
                                        Designation <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        className="form-control"
                                        maxLength={100}
                                        placeholder="Enter designation"
                                        value={form.designation}
                                        onChange={(e) =>
                                            setForm({ ...form, designation: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>Country</label>
                                    <input
                                        className="form-control"
                                        value={form.country}
                                        onChange={(e) =>
                                            setForm({ ...form, country: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>City</label>
                                    <input
                                        className="form-control"
                                        value={form.city}
                                        onChange={(e) =>
                                            setForm({ ...form, city: e.target.value })
                                        }
                                    />
                                </div>

                            </div>

                            <div className="text-end">
                                <button className="btn btn-primary" onClick={handleSubmit}>
                                    Save
                                </button>
                            </div>

                        </div>

                    </div>
                </div>

            </div>
        </>
    );
};

export default SponsorLayer;
